"use client"

import React, { useState, useEffect, useCallback, useRef } from "react"
import { useRouter } from "next/navigation"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { notificationBus } from "@/lib/notification-bus"
import { CheckCircle2, XCircle, AlertCircle, AlertTriangle, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { useWS } from "@/providers/ws-provider"
import { playSound } from "@/lib/audio-service"
import { FUNDS_PAGE_BACK_EVENT } from "@/lib/page-back-forward"
import { apiV1Fetch } from "@/lib/api"
import { DEMO_SESSION_KEY } from "@/providers/demo-context"
import { navigateFromNotificationPath } from "@/lib/notification-navigation"

interface NotificationState {
  open: boolean
  type: "success" | "error" | "info"
  title: string
  message: string
  nextPath?: string
}

interface ToastItem {
  id: number
  type: "success" | "error" | "info"
  title?: string
  message: string
  nextPath?: string
}

// ── Native Notification Helpers ──────────────────────────────────────────────
// Show a native OS notification when the app tab is hidden / minimised.
// Clicking the notification focuses the window and navigates to the target page.
async function showNativeNotification(
  title: string,
  body: string,
  navigatePath?: string,
  notifId?: string | number
) {
  if (typeof window === "undefined" || !("Notification" in window)) return
  if (Notification.permission !== "granted") return

  // Use the same tag format as sw.js so the browser auto-deduplicates
  // if Web Push also delivers the same notification — only one OS notification appears.
  const tag = `push-${notifId || Date.now()}`

  // Try ServiceWorkerRegistration.showNotification first — required on mobile browsers
  // where the `new Notification()` constructor throws "Illegal constructor".
  try {
    const reg = await navigator.serviceWorker?.ready
    if (reg) {
      await reg.showNotification(title, {
        body,
        icon: "/favicon-trader.svg",
        tag,
        renotify: true,
        data: { navigatePath },
      } as any)
      return
    }
  } catch {
    /* SW not available — fall through to constructor */
  }

  // Fallback: desktop browsers support the Notification constructor
  try {
    const notif = new Notification(title, {
      body,
      icon: "/favicon-trader.svg",
      tag,
      renotify: true,
    } as any)
    notif.onclick = () => {
      window.focus()
      notif.close()
      if (navigatePath) {
        window.location.href = navigatePath
      }
    }
  } catch {
    /* Notification constructor not supported */
  }
}

// Request notification permission — called once on mount.
// Only prompts if the user hasn't already granted/denied.
function requestNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return
  if (Notification.permission === "default") {
    Notification.requestPermission()
  }
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const [modalState, setModalState] = useState<NotificationState>({
    open: false,
    type: "info",
    title: "",
    message: "",
    nextPath: undefined,
  })

  const [toasts, setToasts] = useState<ToastItem[]>([])

  // Monotonic id source for toasts. Previously we used Date.now() directly,
  // which produced duplicate React keys when two toasts were enqueued in the
  // same millisecond (notification WS push racing the bus replay, for
  // example) and triggered "Encountered two children with the same key".
  const toastIdRef = useRef(0)

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  // Route a notification click to `path`. Delegates to the shared
  // `navigateFromNotificationPath` helper so the floating toast, the
  // /admin/my-notifications list, and any future entry point stay in
  // sync on same-page-hash handling (see helper for the App Router
  // caveats that motivated the extraction).
  const navigateFromNotification = useCallback(
    (path: string) => navigateFromNotificationPath(path, (href) => router.push(href)),
    [router],
  )

  // Mark every server-side notification as read.
  //
  // Called on toast-body click so the bell badge clears immediately no matter
  // where the toast lands the user (e.g. payment toasts navigate to
  // /trade/funds, not the Updates page, so without this the badge would
  // stay stuck at ≥1). Mirrors the same POST + `clear-unread` event pair
  // that the Updates-page mount effect uses so both entry points converge
  // on identical state. Fire-and-forget: the badge already flipped to 0
  // locally via the event; on POST failure we re-fetch the server count so
  // the badge doesn't lie.
  //
  // Guards: skip if the user isn't logged in (no session cookie to
  // authenticate) or is in cascade demo mode (offline sample data — no
  // server-side notifications to reconcile).
  const markAllNotificationsRead = useCallback(() => {
    try {
      if (typeof window === "undefined") return
      if (!localStorage.getItem("user")) return
      if (sessionStorage.getItem(DEMO_SESSION_KEY) === "true") return
    } catch {
      return
    }
    window.dispatchEvent(new CustomEvent("clear-unread"))
    apiV1Fetch("/api/notifications/read-all", { method: "POST" }).catch(() => {
      window.dispatchEvent(new CustomEvent("refresh-unread-count"))
    })
  }, [])

  // Dedup: prevent the same notification from showing a toast/native twice within
  // the same page lifecycle. Uses a ref (not sessionStorage) so it resets on
  // page reload — this allows catch-up notifications on SSE connect to display
  // once, while still preventing duplicates from SSE reconnections mid-session.
  const shownIdsRef = useRef<Set<string>>(new Set())

  // If the app was opened from a push notification click (cold start / PWA killed),
  // sw.js appends ?from_push=<notificationId> to the URL. Pre-mark that notification
  // as shown so the SSE delivery doesn't trigger a duplicate toast.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const pushId = params.get("from_push")
      if (pushId) {
        shownIdsRef.current.add(pushId)
        // Clean the URL without triggering a navigation
        const url = new URL(window.location.href)
        url.searchParams.delete("from_push")
        window.history.replaceState({}, "", url.pathname + url.search)
      }
    } catch {
      /* ignore */
    }
  }, [])

  // Request notification permission + initialise push notifications.
  // Branches between Capacitor native push and standard Web Push API.
  useEffect(() => {
    if (typeof window === "undefined" || !localStorage.getItem("user")) return

    // Check if running in Capacitor native app (Android/iOS)
    import("@/lib/capacitor-push")
      .then(async ({ isCapacitorNative, initCapacitorPush }) => {
        if (isCapacitorNative()) {
          // Use native push via Capacitor plugin (handles FCM natively)
          await initCapacitorPush()
          return
        }

        // Web browser / PWA — use standard Web Push API
        const { initPushNotifications } = await import("@/lib/push-notifications")
        await initPushNotifications()
      })
      .catch(() => {
        // Capacitor not available — use Web Push as fallback
        import("@/lib/push-notifications")
          .then(async ({ initPushNotifications }) => {
            await initPushNotifications()
          })
          .catch(() => {})
      })

    // Listen for service worker messages:
    // 1. ROLE_CHECK — SW asks "what role is this tab?" via MessageChannel.
    //    Used when multiple tabs are open so the SW can focus the correct one.
    // 2. NOTIFICATION_CLICK — SW has already focused this tab, just navigate.
    const handleSWMessage = (event: MessageEvent) => {
      // Respond to role check from SW (MessageChannel — reply via port)
      if (event.data?.type === "ROLE_CHECK" && event.ports?.[0]) {
        try {
          const stored = localStorage.getItem("user")
          const user = stored ? JSON.parse(stored) : {}
          event.ports[0].postMessage({ role: user.role || "" })
        } catch {
          event.ports[0].postMessage({ role: "" })
        }
        return
      }

      // Navigate on notification click (SW already focused this tab).
      // Route through navigateFromNotification so a same-page hash target
      // (e.g. /admin/payment#recent-topups when already on /admin/payment)
      // triggers the page's hashchange listener instead of silently no-op-ing.
      if (event.data?.type === "NOTIFICATION_CLICK" && event.data?.navigatePath) {
        navigateFromNotification(event.data.navigatePath)
      }
    }
    navigator.serviceWorker?.addEventListener("message", handleSWMessage)

    return () => {
      navigator.serviceWorker?.removeEventListener("message", handleSWMessage)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Listen for Notifications from the WS feed ──
  const { lastNotification } = useWS()

  useEffect(() => {
    if (!lastNotification) return

    const key = String(
      lastNotification.id ?? `${lastNotification.title}:${lastNotification.message}`
    )
    if (shownIdsRef.current.has(key)) return
    shownIdsRef.current.add(key)

    const notifTitle = lastNotification.title || "New Notification"
    const notifMessage = lastNotification.message
    // `path` and `type` are optional payload fields used by system-issued
    // notifications (e.g. payment success/failed/expired → '/trade/funds'
    // with type='success'/'error'). Admin-sent notifications today omit
    // both, so we fall back to the existing defaults to keep them looking
    // the same as before.
    const notifPath = (typeof lastNotification.path === "string" && lastNotification.path)
      ? lastNotification.path
      : "/trade/profile/notifications"
    const rawType = String(lastNotification.type || "").toLowerCase()
    const notifType: "success" | "error" | "info" =
      rawType === "success" ? "success"
      : rawType === "error" ? "error"
      : "info"
    const notifId = lastNotification.id ?? Date.now()

    // When app is in background (tab hidden or unfocused), show a native OS
    // notification so the user gets alerted. The tag matches sw.js's tag format
    // so the browser auto-deduplicates if Web Push also fires for the same
    // notification — only one OS notification will appear, never two.
    // When the app IS focused, show an in-app toast instead.
    const appIsInBackground = document.hidden || !document.hasFocus()

    if (appIsInBackground) {
      showNativeNotification(notifTitle, notifMessage, notifPath, notifId)
    } else {
      notificationBus.toast(notifType, notifMessage, notifTitle, notifPath)
    }

    playSound("notification")
  }, [lastNotification])

  // When the user marks a notification as read in the Updates tab, add it to
  // the shown set so it never pops up as a toast again within this session.
  useEffect(() => {
    const handleRead = (e: Event) => {
      const id = (e as CustomEvent).detail?.id
      if (id != null) shownIdsRef.current.add(String(id))
    }
    window.addEventListener("notification-read", handleRead)
    return () => window.removeEventListener("notification-read", handleRead)
  }, [])

  // ── Replay UNREAD notifications on app open + tab refocus ─────────────────
  // Without this, a notification that arrived while the admin/trader was
  // logged-out (or on another tab) never re-surfaces — the bell badge alone
  // is easy to miss. We fetch unread items via /api/notifications/ and toast
  // ONLY THE LATEST one (the bell badge keeps the full count). Cap of one is
  // intentional: showing 5 stacked toasts blocks the UI; the user came back
  // to do something, not to read a backlog. The dedup via shownIdsRef
  // ensures the in-session WS delivery for the same id doesn't double-toast.
  //
  // Runs on:
  //   1. Mount — initial page load catches notifications that arrived while
  //      the tab was closed or logged out.
  //   2. visibilitychange to visible — catches notifications that arrived
  //      during a hidden-tab period. Debounced to 30 s so rapidly flipping
  //      tabs doesn't fetch/toast over and over.
  const lastReplayAtRef = useRef(0)
  const REPLAY_DEBOUNCE_MS = 30_000

  const replayUnreadLatest = useCallback(async () => {
    if (typeof window === "undefined" || !localStorage.getItem("user")) return
    const now = Date.now()
    if (now - lastReplayAtRef.current < REPLAY_DEBOUNCE_MS) return
    lastReplayAtRef.current = now
    try {
      // Reuse apiFetch with skipErrorNotification so a transient 401 on a
      // freshly-expired session doesn't show the standard error toast.
      const { apiFetch } = await import("@/lib/api")
      const res = await apiFetch("/api/notifications?page=1", { skipErrorNotification: true })
      if (!res.ok) return
      const data = await res.json().catch(() => null)
      if (!data?.success || !Array.isArray(data.records)) return
      // Defensive sort: backend returns ORDER BY n.id DESC, but sorting
      // explicitly here means a future change to the controller can't
      // silently flip the "latest" semantics on us.
      const unread = (data.records as Array<{
        is_read?: boolean
        id?: number | string
        title?: string
        message?: string
        path?: string | null
      }>)
        .filter((n) => !n.is_read)
        .sort((a, b) => Number(b.id ?? 0) - Number(a.id ?? 0))
      const latest = unread[0]
      if (!latest) return
      const key = String(latest.id)
      // Already toasted in this session (e.g. live WS delivered it, or a
      // previous replay toasted it). Don't double-toast.
      if (shownIdsRef.current.has(key)) return
      shownIdsRef.current.add(key)
      const titleLc = (latest.title || "").toLowerCase()
      const type: "success" | "error" | "info" =
        titleLc.includes("rejected") || titleLc.includes("failed")
          ? "error"
          : titleLc.includes("completed") ||
              titleLc.includes("added") ||
              titleLc.includes("approved")
            ? "success"
            : "info"
      // Pass record.path as nextPath so replay-rendered toasts are clickable
      // (e.g. withdrawal-request received → /admin/payment#withdrawal-history).
      // Live WS toasts already pass the path above; this is the parallel for
      // the on-mount / on-refocus replay.
      const replayPath =
        typeof latest.path === "string" && latest.path ? latest.path : undefined
      notificationBus.toast(
        type,
        latest.message || "",
        latest.title || "Notification",
        replayPath,
      )
    } catch {
      /* best-effort — silent on transient failures */
    }
  }, [])

  // Run on mount.
  useEffect(() => {
    replayUnreadLatest()
  }, [replayUnreadLatest])

  // Run on tab refocus (visibilitychange → visible). The 30 s debounce inside
  // replayUnreadLatest prevents toast spam from rapid tab toggling.
  useEffect(() => {
    if (typeof document === "undefined") return
    const handler = () => {
      if (document.visibilityState === "visible") {
        replayUnreadLatest()
      }
    }
    document.addEventListener("visibilitychange", handler)
    return () => document.removeEventListener("visibilitychange", handler)
  }, [replayUnreadLatest])

  // ── Listen for Bus Events ──
  useEffect(() => {
    const unsubscribe = notificationBus.subscribe((event) => {
      if (event.displayMode === "toast") {
        toastIdRef.current += 1
        const id = toastIdRef.current
        setToasts((prev) => [
          ...prev,
          {
            id,
            type: event.type,
            title: event.title,
            message: event.message,
            nextPath: event.nextPath,
          },
        ])
        setTimeout(() => removeToast(id), 5000)
      } else {
        setModalState({
          open: true,
          type: event.type,
          title: event.title,
          message: event.message,
          nextPath: event.nextPath,
        })
      }
    })

    return unsubscribe
  }, [removeToast])

  // True when this modal is a session-termination flow (Session Expired /
  // Account Blocked / Settings Updated / Logged out via cross-tab). Drives
  // both the dismissibility lockdown and the "Login Again" button behaviour.
  const isSessionEndModal =
    modalState.type === "error" &&
    !!modalState.nextPath &&
    /\/(login)\/?$/.test(modalState.nextPath)

  // Close the modal WITHOUT firing the nextPath navigation. Used when the
  // dismiss came from a system-level back action (mobile system back or
  // the funds-page header chevron broadcasting `funds-page-back`) — the
  // user's intent is "go back," not "advance to the modal's nextPath."
  // Skips entirely for session-end modals so they remain non-dismissible.
  //
  // Listener registration is deferred behind a short setTimeout: the
  // most common modal trigger (a form-submit success) calls
  // `notificationBus.success()` AND `router.back()/replace()` in the
  // same tick — the navigation fires a popstate right after the modal
  // opens. Without the defer, that popstate would close the modal
  // before the user ever sees it. ~150ms is plenty for the
  // navigation-induced popstate to drain while staying short enough
  // that any genuine back tap is still handled instantly.
  useEffect(() => {
    if (!modalState.open || isSessionEndModal || typeof window === "undefined") return
    const closeOnly = () => setModalState((prev) => ({ ...prev, open: false }))
    let attached = false
    const armId = window.setTimeout(() => {
      attached = true
      // Mobile system back: the browser has already popped one entry by
      // the time popstate fires; URL has changed. Closing the modal
      // prevents it from ghosting on top of the new page.
      window.addEventListener("popstate", closeOnly)
      // Funds-page chevron broadcast: the chevron emits this just before
      // calling `router.back()`. Closing here means the modal disappears
      // in the same tap as the navigation, instead of ghosting onto the
      // next page (NotificationProvider is mounted on every trader page).
      window.addEventListener(FUNDS_PAGE_BACK_EVENT, closeOnly)
    }, 150)
    return () => {
      window.clearTimeout(armId)
      if (attached) {
        window.removeEventListener("popstate", closeOnly)
        window.removeEventListener(FUNDS_PAGE_BACK_EVENT, closeOnly)
      }
    }
  }, [modalState.open, isSessionEndModal])

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, open: false }))
    if (modalState.nextPath) {
      // Any error modal whose nextPath is a login route is a session-
      // termination flow (Session Expired, User Not Found, Account Blocked,
      // Settings Updated, ...). Force a HARD reload so all in-memory React
      // state (WS subscriptions, tick cache, refcounts, route stores)
      // flushes — a soft router.push would leave the stale tree mounted and
      // cascading 401s would re-fire from background hooks. Other modals
      // (success, info, non-login-redirect errors) keep soft navigation.
      if (isSessionEndModal && typeof window !== "undefined") {
        window.location.replace(modalState.nextPath)
        return
      }
      router.push(modalState.nextPath)
      return
    }
    // No nextPath, but this is still a session-end flow (e.g. a future
    // regression where the caller forgot to pass it). Fall back to the
    // trader login screen so the user can't be stranded on a page whose
    // localStorage.user was already cleared by triggerSessionExpiry.
    if (isSessionEndModal && typeof window !== "undefined") {
      window.location.replace("/trade/login")
    }
  }

  // Every session-end modal (Session Expired, Settings Updated, Account
  // Blocked, Logged-out cross-tab, etc — anything that redirects to a /login
  // route) is an advisory "please log back in" state, not a catastrophic
  // error. Show them in warning (amber) tones with an alert-triangle icon
  // so the UI doesn't feel as alarming as a real application error. Reuses
  // the existing `isSessionEndModal` boolean above so the styling stays in
  // lockstep with the non-dismissibility + "Login Again" button contract.
  // Non-session-end bus.error() modals (form-validation failures, etc.) keep
  // the destructive red XCircle treatment.
  const ModalIcon = isSessionEndModal
    ? AlertTriangle
    : {
        success: CheckCircle2,
        error: XCircle,
        info: AlertCircle,
      }[modalState.type]

  const modalColors = isSessionEndModal
    ? "text-warning"
    : {
        success: "text-success",
        error: "text-destructive",
        info: "text-primary",
      }[modalState.type]

  return (
    <>
      {children}

      {/* ── Toasts Container ── */}
      <div className="pointer-events-none fixed left-4 right-4 top-4 z-[9999] flex flex-col gap-3 md:left-auto md:right-6 md:top-6 md:w-full md:max-w-[400px]">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              "pointer-events-auto relative overflow-hidden rounded-xl border p-4 shadow-2xl transition-all duration-300 animate-in slide-in-from-right-full",
              "bg-card/95 backdrop-blur-xl",
              toast.type === "success" && "border-success/30 shadow-success/20",
              toast.type === "error" && "border-destructive/30 shadow-destructive/20",
              toast.type === "info" && "border-primary/30 shadow-primary/20",
              toast.nextPath && "cursor-pointer transition-colors hover:border-primary/60"
            )}
            onClick={() => {
              // Only toasts with a nextPath represent server-side
              // notifications (locally-generated success/error toasts from
              // notificationBus have none). Marking read + navigating only
              // applies to the former; the latter stays a purely visual
              // dismiss on click and X.
              //
              // Route through navigateFromNotification instead of a plain
              // router.push so a same-page hash target actually triggers the
              // destination page's hashchange listener (tab switch + scroll
              // to anchor). Plain router.push silently no-ops for
              // same-pathname-different-hash — that's the exact bug this
              // fixes for the payment-page topup deep-link.
              if (toast.nextPath) {
                markAllNotificationsRead()
                navigateFromNotification(toast.nextPath)
                removeToast(toast.id)
              }
            }}
          >
            {/* Progress Bar */}
            <div
              className={cn(
                "duration-[5000ms] absolute bottom-0 left-0 h-1 w-full origin-left bg-current opacity-30 transition-all ease-linear",
                toast.type === "success" && "text-success",
                toast.type === "error" && "text-destructive",
                toast.type === "info" && "text-primary"
              )}
              style={{ animation: "toast-progress 5s linear forwards" }}
            />

            <div className="flex gap-4">
              <div
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                  toast.type === "success" && "bg-success/20 text-success",
                  toast.type === "error" && "bg-destructive/20 text-destructive",
                  toast.type === "info" && "bg-primary/20 text-primary"
                )}
              >
                {toast.type === "success" && <CheckCircle2 className="h-6 w-6" />}
                {toast.type === "error" && <XCircle className="h-6 w-6" />}
                {toast.type === "info" && <AlertCircle className="h-6 w-6" />}
              </div>

              <div className="flex-1 pt-1">
                {toast.title && (
                  <h5 className="mb-1 text-sm font-black uppercase leading-none tracking-widest text-foreground">
                    {toast.title}
                  </h5>
                )}
                <p className="text-sm font-bold leading-snug tracking-tight text-muted-foreground">
                  {toast.message}
                </p>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation()
                  removeToast(toast.id)
                }}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-muted"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── Modal Dialog ──
          Session-termination modals (Session Expired / Account Blocked /
          Settings Updated / Logged-out cross-tab) are non-dismissible by
          Escape, overlay tap, or close-X — the only exit is the Login Again
          button. Otherwise users could close the dialog and remain on a page
          whose React state still references the now-cleared user, racing
          background hooks against the 1.5s failsafe redirect in api.ts. */}
      <Dialog
        open={modalState.open}
        onOpenChange={(open) => {
          if (open) return
          if (isSessionEndModal) return // ignore dismiss attempts; only the button exits
          closeModal()
        }}
      >
        <DialogContent
          hideClose={isSessionEndModal}
          onEscapeKeyDown={(e) => { if (isSessionEndModal) e.preventDefault() }}
          onPointerDownOutside={(e) => { if (isSessionEndModal) e.preventDefault() }}
          onInteractOutside={(e) => { if (isSessionEndModal) e.preventDefault() }}
          className={cn(
            "flex max-w-[90%] flex-col items-center justify-center border border-border/50 p-6 shadow-2xl sm:max-w-[320px] sm:rounded-2xl",
            "bg-card text-foreground"
          )}
        >
          <DialogHeader className="flex flex-col items-center justify-center space-y-2 text-center">
            <div
              className={cn(
                "mb-1 rounded-full p-4",
                isSessionEndModal
                  ? "bg-warning/10"
                  : modalState.type === "success"
                    ? "bg-success/10"
                    : modalState.type === "error"
                      ? "bg-destructive/10"
                      : "bg-muted"
              )}
            >
              <ModalIcon className={cn("h-10 w-10", modalColors)} strokeWidth={2.5} />
            </div>
            <DialogTitle
              className={cn(
                "text-center text-xl font-bold uppercase tracking-tight",
                isSessionEndModal
                  ? "text-warning"
                  : modalState.type === "success"
                    ? "text-success"
                    : modalState.type === "error"
                      ? "text-destructive"
                      : "text-foreground"
              )}
            >
              {modalState.title ||
                (modalState.type === "success"
                  ? "Success"
                  : modalState.type === "error"
                    ? "Error"
                    : "Notification")}
            </DialogTitle>
            <DialogDescription className="px-6 text-center text-[14px] font-semibold leading-snug tracking-tight text-foreground/70">
              {modalState.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex w-full items-center justify-center sm:justify-center">
            <Button
              onClick={closeModal}
              className={cn(
                "h-10 min-w-[140px] rounded-xl text-[11px] font-bold uppercase tracking-widest shadow-sm transition-all active:scale-95",
                isSessionEndModal
                  ? "bg-warning text-warning-foreground hover:bg-warning/90"
                  : modalState.type === "success"
                    ? "bg-success text-success-foreground hover:bg-success/90"
                    : modalState.type === "error"
                      ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      : "bg-primary text-primary-foreground"
              )}
            >
              {modalState.type === "success"
                ? "Continue"
                : modalState.type === "error" &&
                    modalState.nextPath &&
                    /\/(login)\/?$/.test(modalState.nextPath)
                  ? "Login Again"
                  : "Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style jsx global>{`
        @keyframes toast-progress {
          from {
            transform: scaleX(1);
          }
          to {
            transform: scaleX(0);
          }
        }
      `}</style>
    </>
  )
}
